import {
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Stack,
} from "@mui/material";
import { OrionIconButton } from "@orion/OrionComponents/OrionIconButton";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";

import { ActionsContainer } from "./FrameCardActions.styles";
import { resolveTextCardVariant } from "./frameVariant";
import type { SupportedMedium } from "./types";
import type { Frame } from "@/types/dragon-tiles";

import {
  linkedBroadcastsForFrameQueryOptions,
  type LinkedBroadcast,
} from "@/api/broadcastV2Audiences";
import { useDeleteFrame, frameQueryOptions } from "@/api/dragon-tiles";
import { useSendTextPreviewTestSend } from "@/api/dragon-tiles/textPreviewTestSend";
import {
  SMS_NOT_ENABLED_REASON,
  SMS_NOT_ENABLED_TITLE,
  useIsTextMessagingActive,
} from "@/api/siteCapabilities";
import { DeleteConfirmationModal } from "@/components/DeleteConfirmationModal";
import { DuplicateFrame } from "@/components/DuplicateFrame/DuplicateFrame";
import { LinkedBroadcastsDeleteBlockedModal } from "@/components/LinkedBroadcastsDeleteBlockedModal";
import { SmsNotEnabledBanner } from "@/components/SmsNotEnabledBanner";
import { TestEmailBar } from "@/components/TestEmailBar/TestEmailBar";
import {
  TestSmsBar,
  type TestSmsAdapter,
} from "@/components/TestSmsBar/TestSmsBar";
import { textComposerFrameVariantContentJsonSchema } from "@/features/TextComposerV2/frame/frameOutput";
import { getSmsAutocompleteStorageKey } from "@/features/TextComposerV2/preview/recipientPhones";
import {
  getEmailVariantPlaintextBody,
  getEmailVariantPreviewHtml,
} from "@/frames/service";
import { useTestEmail } from "@/hooks/useTestEmail";
import {
  AddIcon,
  ContentCopyIcon,
  DeleteIcon,
  EditOutlinedIcon,
  VisibilityOutlinedIcon,
} from "@/icons";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

interface FrameCardActionsProps {
  frame: Frame;
  siteId: string;
  selectedMedium: SupportedMedium | null;
  /** Visual variant - "card" for hover overlay, "drawer" for inline square icon buttons, "inline" for inline standard icon buttons */
  variant?: "card" | "drawer" | "inline";
  /** Hide the preview button (useful when already in preview drawer) */
  hidePreview?: boolean;
  /** Callback when delete succeeds (useful for navigation) */
  onDeleteSuccess?: () => void;
  /** Notifies parent when any owned modal (duplicate/delete) is open. Parent can use this to stay mounted while modal is up. */
  onModalOpenChange?: (open: boolean) => void;
}

export function FrameCardActions({
  frame,
  siteId,
  selectedMedium,
  variant = "card",
  hidePreview = false,
  onDeleteSuccess,
  onModalOpenChange,
}: FrameCardActionsProps) {
  const deleteFrame = useDeleteFrame();
  const queryClient = useQueryClient();
  const currentSearch = useSearch({
    from: "/_auth/creative-sphere/$siteId",
  });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [isLinkedDeleteBlockedModalOpen, setIsLinkedDeleteBlockedModalOpen] =
    useState(false);
  const [linkedBroadcastsBlockingDelete, setLinkedBroadcastsBlockingDelete] =
    useState<LinkedBroadcast[]>([]);
  const [isLinkedBroadcastCheckPending, setIsLinkedBroadcastCheckPending] =
    useState(false);

  useEffect(() => {
    onModalOpenChange?.(
      isDeleteModalOpen ||
        isDuplicateModalOpen ||
        isLinkedDeleteBlockedModalOpen,
    );
  }, [
    isDeleteModalOpen,
    isDuplicateModalOpen,
    isLinkedDeleteBlockedModalOpen,
    onModalOpenChange,
  ]);

  // Check if the selected medium has a variant (sms also matches mms)
  const hasSelectedMedium = selectedMedium
    ? frame.frame_variants.some(
        (v) =>
          v.medium === selectedMedium ||
          (selectedMedium === "sms" && v.medium === "mms"),
      )
    : false;

  // Get email variant for test email
  const emailVariant = frame.frame_variants.find((v) => v.medium === "email");
  const isEmailMedium =
    selectedMedium === "email" || selectedMedium === "email-mobile";

  const textCardVariant = useMemo(() => resolveTextCardVariant(frame), [frame]);
  const canSendTestSms = textCardVariant !== null;
  // A site without text messaging activated can't send a text test (mirrors
  // the broadcast schedule gate — SMS-enabled only, not RCS).
  const isTextActive = useIsTextMessagingActive(siteId);

  const resolveTestEmailBody = useCallback(async () => {
    const listedBody = getEmailVariantPreviewHtml(emailVariant);
    if (listedBody) return listedBody;
    if (!emailVariant?.id) return "";
    const fullFrame = await queryClient.ensureQueryData(
      frameQueryOptions(siteId, frame.id),
    );
    return getEmailVariantPreviewHtml(
      fullFrame.frame_variants.find((v) => v.id === emailVariant.id),
    );
  }, [queryClient, siteId, frame.id, emailVariant]);

  // Listing rows carry no content fields, so the plaintext body may only be
  // resolvable after the full-frame fetch (deduped with resolveTestEmailBody's).
  const resolveTestEmailPlaintext = useCallback(async () => {
    if (!emailVariant?.id) return undefined;
    const listed = getEmailVariantPlaintextBody(emailVariant);
    if (listed !== undefined) return listed;
    if (emailVariant.content_text) return undefined;
    const fullFrame = await queryClient.ensureQueryData(
      frameQueryOptions(siteId, frame.id),
    );
    return getEmailVariantPlaintextBody(
      fullFrame.frame_variants.find((v) => v.id === emailVariant.id),
    );
  }, [queryClient, siteId, frame.id, emailVariant]);

  const resolveTextTestSend = useCallback(async () => {
    const fullFrame = await queryClient.ensureQueryData(
      frameQueryOptions(siteId, frame.id),
    );
    const resolved = resolveTextCardVariant(fullFrame);
    if (!resolved) return null;
    const parsed = textComposerFrameVariantContentJsonSchema.safeParse(
      resolved.variant.content_json,
    );
    if (!parsed.success || parsed.data.primaryChannel === "INVALID") {
      return null;
    }
    return {
      medium: resolved.medium,
      content_json: parsed.data,
      config: resolved.variant.config ?? {},
    };
  }, [queryClient, siteId, frame.id]);

  // Test email setup
  const { adapter: testEmailAdapter, scope: testEmailScope } = useTestEmail({
    siteId,
    frameId: frame.id,
    frameVariantId: emailVariant?.id ?? "",
    basePayload: {
      body: getEmailVariantPreviewHtml(emailVariant),
      plaintext: getEmailVariantPlaintextBody(emailVariant),
      subject: emailVariant?.config?.subject_line || "Test: Subject Line",
      preheader: emailVariant?.config?.preview_text || "Test: Preheader",
      product_grids: emailVariant?.config?.product_grids,
      offers: emailVariant?.config?.offers,
    },
    resolveBody: resolveTestEmailBody,
    resolvePlaintext: resolveTestEmailPlaintext,
  });

  // Test SMS adapter
  const testSmsAdapter: TestSmsAdapter<{
    siteId: string;
  }> = {
    storageKey: ({ siteId }) => getSmsAutocompleteStorageKey(siteId),
    useHandler: ({ siteId }) => {
      const previewMutation = useSendTextPreviewTestSend(siteId);
      const [isResolving, setIsResolving] = useState(false);
      return {
        onSend: (phones: string[]) => {
          void (async () => {
            setIsResolving(true);
            try {
              const resolved = await resolveTextTestSend();
              if (!resolved) {
                enqueueErrorSnackbar(
                  null,
                  "This tile has no sendable text content",
                );
                return;
              }
              previewMutation.mutate({
                site_id: siteId,
                medium: resolved.medium,
                phone: phones,
                content_json: resolved.content_json,
                config: resolved.config,
                customerId: "",
              });
            } finally {
              setIsResolving(false);
            }
          })();
        },
        isSending: isResolving || previewMutation.isPending,
      };
    },
  };

  const editVariant = selectedMedium === "sms" ? "text" : "email";

  const editLinkProps = {
    component: Link,
    to: "/dragon-tiles/$siteId/$frameId/$variant",
    params: { siteId, frameId: frame.id, variant: editVariant },
    search: {
      returnTo: "creative-sphere",
      medium: currentSearch.medium,
      dragon_tile: currentSearch.dragon_tile,
      event: currentSearch.event,
      order_by: currentSearch.order_by,
      order: currentSearch.order,
      search: currentSearch.search,
      view: currentSearch.view,
      preview: currentSearch.preview,
      deactivated: currentSearch.deactivated,
    },
  };

  const previewLinkProps = {
    component: Link,
    to: "/creative-sphere/$siteId" as const,
    params: { siteId },
    search: {
      medium: currentSearch.medium,
      dragon_tile: currentSearch.dragon_tile,
      event: currentSearch.event,
      order_by: currentSearch.order_by,
      order: currentSearch.order,
      search: currentSearch.search,
      view: currentSearch.view,
      deactivated: currentSearch.deactivated,
      is_active: currentSearch.is_active,
      expectedRPM: currentSearch.expectedRPM,
      preview: frame.id,
    },
  };

  const handleDeleteButtonClick = async () => {
    setIsLinkedBroadcastCheckPending(true);
    try {
      const linked = await queryClient.fetchQuery(
        linkedBroadcastsForFrameQueryOptions(siteId, frame.id),
      );
      if (linked.length > 0) {
        setLinkedBroadcastsBlockingDelete(linked);
        setIsLinkedDeleteBlockedModalOpen(true);
        return;
      }
      setIsDeleteModalOpen(true);
    } catch (error) {
      enqueueErrorSnackbar(
        error,
        "Could not verify whether this tile is linked to broadcasts",
      );
      setIsDeleteModalOpen(true);
    } finally {
      setIsLinkedBroadcastCheckPending(false);
    }
  };

  const handleCloseLinkedDeleteBlockedModal = () => {
    setIsLinkedDeleteBlockedModalOpen(false);
    setLinkedBroadcastsBlockingDelete([]);
  };

  const handleDelete = () => {
    void deleteFrame.mutate(
      {
        siteId,
        frameId: frame.id,
        innerTileId: frame.inner_tile,
      },
      {
        onSuccess: () => {
          onDeleteSuccess?.();
        },
        onSettled: () => {
          setIsDeleteModalOpen(false);
        },
      },
    );
  };

  const isDrawer = variant === "drawer";
  const ButtonComponent = isDrawer ? OrionIconButton : IconButton;
  const buttonProps = isDrawer
    ? { variant: "outlined" as const }
    : { sx: { color: "text.primary" } };

  const actionButtons = (
    <>
      <Tooltip title="Edit" placement="top">
        <span>
          <ButtonComponent
            {...buttonProps}
            {...(hasSelectedMedium ? editLinkProps : {})}
            size="small"
            aria-label="Edit"
            disabled={!hasSelectedMedium}
          >
            <EditOutlinedIcon fontSize="small" />
          </ButtonComponent>
        </span>
      </Tooltip>
      <DuplicateFrame
        frame={frame}
        siteId={siteId}
        onOpenChange={setIsDuplicateModalOpen}
      >
        {({ open, isPending }) => (
          <Tooltip
            title={isPending ? "Duplicating..." : "Duplicate"}
            placement="top"
          >
            <span>
              <ButtonComponent
                {...buttonProps}
                onClick={open}
                size="small"
                aria-label="Duplicate"
                disabled={isPending}
              >
                {isPending ? (
                  <CircularProgress size={16} />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </ButtonComponent>
            </span>
          </Tooltip>
        )}
      </DuplicateFrame>
      {isEmailMedium && emailVariant && (
        <TestEmailBar
          scope={testEmailScope}
          adapter={testEmailAdapter}
          siteId={siteId}
          buttonType="icon"
          {...(isDrawer
            ? { buttonVariant: "outlined" }
            : { iconColor: "text.primary" })}
          disabled={!emailVariant}
        />
      )}
      {selectedMedium === "sms" && (
        <TestSmsBar
          scope={{ siteId }}
          adapter={testSmsAdapter}
          buttonType="icon"
          {...(isDrawer
            ? { buttonVariant: "outlined" }
            : { iconColor: "text.primary" })}
          disabled={!canSendTestSms || !isTextActive}
          locked={!isTextActive}
          lockedReason={`${SMS_NOT_ENABLED_TITLE}. ${SMS_NOT_ENABLED_REASON}`}
          lockedTooltip={<SmsNotEnabledBanner siteId={siteId} />}
        />
      )}
      {!hidePreview && (
        <Tooltip title="Preview" placement="top">
          <span>
            <ButtonComponent
              {...buttonProps}
              {...previewLinkProps}
              size="small"
              aria-label="Preview"
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </ButtonComponent>
          </span>
        </Tooltip>
      )}
      <Tooltip title="Delete" placement="top">
        <span>
          <ButtonComponent
            {...buttonProps}
            onClick={() => {
              void handleDeleteButtonClick();
            }}
            size="small"
            aria-label="Delete"
            disabled={deleteFrame.isPending || isLinkedBroadcastCheckPending}
            sx={
              isDrawer
                ? {
                    borderColor: "error.main",
                    color: "error.main",
                    "&:hover": {
                      borderColor: "error.dark",
                      backgroundColor: "error.light",
                    },
                  }
                : { color: "error.main" }
            }
          >
            {deleteFrame.isPending || isLinkedBroadcastCheckPending ? (
              <CircularProgress size={16} />
            ) : (
              <DeleteIcon fontSize="small" />
            )}
          </ButtonComponent>
        </span>
      </Tooltip>
    </>
  );

  return (
    <>
      <LinkedBroadcastsDeleteBlockedModal
        open={isLinkedDeleteBlockedModalOpen}
        broadcasts={linkedBroadcastsBlockingDelete}
        onClose={handleCloseLinkedDeleteBlockedModal}
      />
      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        isPending={deleteFrame.isPending}
        contentText={`Are you sure you want to delete this tile? This action cannot be undone.`}
      />
      {variant === "card" ? (
        <ActionsContainer
          showCreateButton={!hasSelectedMedium && !!selectedMedium}
        >
          <Stack direction="row" spacing={3} justifyContent="center">
            {actionButtons}
          </Stack>
          {!hasSelectedMedium && selectedMedium && (
            <Button
              variant="contained"
              fullWidth
              startIcon={<AddIcon />}
              aria-label={`Create ${selectedMedium}`}
              component={Link}
              to="/dragon-tiles/$siteId/$frameId/$variant"
              params={{
                siteId,
                frameId: frame.id,
                // email-desktop and email-mobile both map to the "email" variant
                variant: selectedMedium === "sms" ? "text" : "email",
              }}
              search={{
                returnTo: "creative-sphere",
                medium: currentSearch.medium,
                dragon_tile: currentSearch.dragon_tile,
                event: currentSearch.event,
                order_by: currentSearch.order_by,
                order: currentSearch.order,
                search: currentSearch.search,
                view: currentSearch.view,
                preview: currentSearch.preview,
                deactivated: currentSearch.deactivated,
              }}
              sx={{
                mx: -2,
                mt: 3,
                width: "calc(100% + 32px)",
                borderRadius: 5,
                cornerShape: "bevel",
              }}
            >
              Create {selectedMedium}
            </Button>
          )}
        </ActionsContainer>
      ) : (
        <Stack direction="row" spacing={2}>
          {actionButtons}
        </Stack>
      )}
    </>
  );
}
